import { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "How does GreenTick improve the recruitment process?",
      answer: "GreenTick uses advanced AI to automate initial screening, conduct intelligent interviews, and provide detailed candidate insights. This reduces time-to-hire by up to 10x while improving candidate quality."
    },
    {
      question: "Who can benefit from using GreenTick?",
      answer: "GreenTick is designed for corporates looking to streamline hiring, educational institutes managing placements, and individuals preparing for interviews. Our platform scales to meet diverse recruitment needs."
    },
    {
      question: "How does the conversational AI interview work?",
      answer: "Our AI conducts natural conversations with candidates, asking relevant questions based on the job role. It adapts questions dynamically based on responses, evaluates answers in real-time, and provides comprehensive reports."
    },
    {
      question: "How does GreenTick ensure data privacy and security?",
      answer: "We employ enterprise-grade encryption, comply with GDPR and data protection regulations, and ensure all candidate data is stored securely. Your data privacy is our top priority."
    },
    {
      question: "Can candidates see their feedback and results?",
      answer: "Yes, candidates receive instant feedback after interviews, including performance scores and areas for improvement. This transparency enhances the candidate experience and helps them grow."
    }
  ];

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section id="faq" className="py-32 bg-transparent relative overflow-hidden">

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500/10 rounded-full mb-6 border border-emerald-500/20">
            <HelpCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">FAQ</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Questions About GreenTick?
          </h2>
          <p className="text-xl text-slate-300">
            We have answers!
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity"></div>
              <div className="relative glass-dark rounded-2xl shadow-lg border border-emerald-500/20 overflow-hidden group-hover:border-emerald-500/50 transition-colors">
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="text-lg font-bold text-white pr-8">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-6 h-6 text-emerald-400 flex-shrink-0 transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6 text-slate-300 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
